import type { ThemeConfig } from 'antd';
// Ant Doesnt support OKLCH yet... https://github.com/ant-design/ant-design/issues/48698
const lightColors = {
  background: '#F9FAFB',      // clean, neutral light background
  surface: '#FFFFFF',        // cards / panels
  textPrimary: '#111827',     // strong readable text
  textMuted: '#5d636f',       // secondary text
  border: '#E5E7EB',          // subtle separation
  primary: '#60ad81',         // your green
  success: '#059669',         // darker emerald green for better contrast
  successBg: '#D1FAE5',       // light mint background
  warning: '#F59E0B',
  error: '#DC2626',
  // Link green is darker than `primary` so inline text on white meets WCAG AA.
  link: '#059669',            // emerald-600, ~4.55:1 on white
  linkHover: '#10b981',       // emerald-500, brand green
  linkActive: '#047857',      // emerald-700, pressed
  // Hand-picked tints for primary-tinted backgrounds. Ant's auto-derived
  // colorPrimaryBg from #60ad81 reads muddy; emerald-50/100 are cleaner.
  primaryBg: '#ECFDF5',       // emerald-50
  primaryBgHover: '#D1FAE5',  // emerald-100
};


const darkColors = {
  background: '#0d1117',                  // matches website --surface-page
  surface: 'rgb(22, 27, 34)',             // matches website --surface-card
  surfaceElevated: '#1c2128',             // dropdowns, popovers, modals
  headerBg: 'rgb(13, 18, 25)',            // matches website --surface-header
  textPrimary: '#f3f4f6',                 // matches website --text-strong
  textSecondary: '#b1b8c2',               // matches website --text-secondary
  textMuted: '#8b949e',                   // matches website --text-muted
  textSubtle: '#6e7681',                  // matches website --text-subtle
  border: '#2c333d',                      // matches website --border-default
  borderSubtle: '#21262d',                // matches website --border-subtle
  primary: '#5bb281',
  success: '#22C55E',
  warning: '#FBBF24',
  error: '#EF4444',
  // Link green is lighter than `primary` so it stays legible on the near-black background.
  link: '#34d399',            // emerald-400
  linkHover: '#6ee7b7',       // emerald-300, brighter
  linkActive: '#10b981',      // emerald-500
  // Translucent emerald overlays so the tint blends with the dark surface
  // instead of producing the desaturated grayish-green Ant derives by default.
  primaryBg: 'rgba(52, 211, 153, 0.12)',
  primaryBgHover: 'rgba(52, 211, 153, 0.20)',
  colorBgContainer: 'rgb(13, 18, 25)',
};

const baseToken = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontSize: 14,
  borderRadius: 6,
  controlHeight: 32,
};

// Sign-in backdrop. Sits outside the Ant token sets: it is the same in light and dark,
// and it is decoration rather than UI surface.
export const loginBackdrop = {
  background: '#4c7d6d',
};

// Inline side menus: full-width square rows with a bar on the selected row's trailing edge.
// activeBarBorderWidth must be 0 or Ant widens every row by that amount and draws its own
// border on the menu, doubling the sider border.
// Ant colors the title of the section that holds the selected page in every state; the shared
// SideMenuBar restores that color only while the section is closed.
const menuAccentBar = {
  activeBarWidth: 3,
  activeBarBorderWidth: 0,
  itemMarginInline: 0,
  itemBorderRadius: 0,
};

export const lightTheme: ThemeConfig = {
  token: {
    ...baseToken,
    colorPrimary: lightColors.primary,
    colorPrimaryBg: lightColors.primaryBg,
    colorPrimaryBgHover: lightColors.primaryBgHover,
    colorLink: lightColors.link,
    colorLinkHover: lightColors.linkHover,
    colorLinkActive: lightColors.linkActive,
    colorSuccess: lightColors.success,
    colorSuccessBg: lightColors.successBg,
    colorSuccessBorder: lightColors.success,
    colorWarning: lightColors.warning,
    colorError: lightColors.error,
    colorBgBase: lightColors.background,
    colorBgContainer: lightColors.surface,
    colorBgLayout: lightColors.background,
    colorText: lightColors.textPrimary,
    colorTextSecondary: lightColors.textMuted,
    colorBorder: lightColors.border,
  },
  components: {
    Layout: { headerBg: lightColors.background, siderBg: lightColors.surface, bodyBg: lightColors.background },
    Menu: {
      ...menuAccentBar,
      itemSelectedBg: lightColors.primaryBgHover,
      // emerald-600 on the emerald-100 row is 3.3:1; emerald-700 clears 4.5:1 for 14px labels.
      itemSelectedColor: lightColors.linkActive,
      subMenuItemSelectedColor: lightColors.textPrimary,
      itemHoverBg: 'rgba(0, 0, 0, 0.04)',
      subMenuItemBg: lightColors.background,
    },
    Card: { boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.69)' },
    Tag: {
      defaultBg: '#E0F2FE',
      defaultColor: '#075985',
    },
    Alert: {
      colorSuccessBg: '#ECFDF5',
      colorSuccessBorder: '#A7F3D0',
    },
    // At rest antd paints the track and handle in colorPrimaryBorder, a pale tint of the primary.
    Slider: {
      railBg: lightColors.border,
      railHoverBg: '#D1D5DB',
      trackBg: lightColors.primary,
      trackHoverBg: lightColors.link,
      handleColor: lightColors.primary,
      handleActiveColor: lightColors.link,
      dotBorderColor: '#D1D5DB',
      dotActiveBorderColor: lightColors.primary,
    },
  },
};

export const darkTheme: ThemeConfig = {
  token: {
    ...baseToken,
    colorPrimary: darkColors.primary,
    colorPrimaryBg: darkColors.primaryBg,
    colorPrimaryBgHover: darkColors.primaryBgHover,
    colorLink: darkColors.link,
    colorLinkHover: darkColors.linkHover,
    colorLinkActive: darkColors.linkActive,
    colorSuccess: darkColors.success,
    colorWarning: darkColors.warning,
    colorError: darkColors.error,
    colorBgBase: darkColors.background,
    colorBgContainer: darkColors.surface,
    colorBgElevated: darkColors.surfaceElevated,
    colorBgSpotlight: darkColors.surfaceElevated,
    colorBgLayout: darkColors.background,
    colorText: darkColors.textPrimary,
    colorTextSecondary: darkColors.textSecondary,
    colorTextTertiary: darkColors.textMuted,
    colorTextQuaternary: darkColors.textSubtle,
    colorBorder: darkColors.border,
    colorBorderSecondary: darkColors.borderSubtle,
    colorFill: 'rgba(255, 255, 255, 0.04)',
    colorFillSecondary: 'rgba(255, 255, 255, 0.06)',
    colorFillTertiary: 'rgba(255, 255, 255, 0.04)',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.02)',
  },
  components: {
    Layout: {
      headerBg: darkColors.headerBg,
      siderBg: darkColors.surface,
      bodyBg: darkColors.background,
      triggerBg: darkColors.surface,
    },
    Menu: {
      ...menuAccentBar,
      itemBg: 'transparent',
      itemSelectedBg: darkColors.primaryBg,
      itemSelectedColor: darkColors.link,
      subMenuItemSelectedColor: darkColors.textPrimary,
      itemHoverBg: 'rgba(255, 255, 255, 0.04)',
      subMenuItemBg: 'rgba(0, 0, 0, 0.25)',
    },
    Card: {
      colorBgContainer: darkColors.surface,
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.5)',
    },
    // The default border blends into the card surface, so an unchecked box nearly vanishes.
    Checkbox: {
      colorBorder: darkColors.textSubtle,
    },
    // Same reason as the light theme Slider override; the dark algorithm mutes the tint further.
    Slider: {
      railBg: 'rgba(255, 255, 255, 0.12)',
      railHoverBg: 'rgba(255, 255, 255, 0.18)',
      trackBg: darkColors.primary,
      trackHoverBg: darkColors.link,
      handleColor: darkColors.primary,
      handleActiveColor: darkColors.link,
      dotBorderColor: 'rgba(255, 255, 255, 0.25)',
      dotActiveBorderColor: darkColors.primary,
    },
  },
};
